<!--
  LiveAudioTrack.vue
  A recording in progress drawn the way an editor's audio track records: the waveform grows from the left as the
  person speaks, behind a record head, and scrolls once it reaches the right edge. It freezes in place while
  `paused` (the head turns amber) and carries on where it left off. Same bars as AudioTrackPlayer (AudioWaveform),
  so the recording looks the same later in the Recording tab.
  It listens to `stream` through its own AudioContext and never stops the caller's tracks. For a plain mic check
  before anything is recorded, use the telehealth AudioVisualizer instead.
-->
<template>
  <div class="live-audio-track" :data-paused="paused">
    <AudioWaveform class="live-audio-track-wave" :peaks="view" :peaksPerSecond="SLICES_PER_SECOND" :start="windowStart"
      :end="windowStart + windowSeconds" @resize="width = $event.width" />
    <div class="live-audio-track-head" :style="{ transform: `translateX(${headX}px)` }"></div>
  </div>
</template>

<script>
import AudioWaveform from './AudioWaveform.vue'

// One bar per 100 ms slice; 30 px per second puts each slice on AudioWaveform's 3 px bar pitch
const SLICE_SECONDS = 0.1
const SLICES_PER_SECOND = 1 / SLICE_SECONDS
const PIXELS_PER_SECOND = 30
// Once full, the head stays this far from the right edge
const HEAD_MARGIN_PX = 12
// Two minutes of slices before the history first doubles
const INITIAL_SLICES = 1200

export default {
  name: 'LiveAudioTrack',
  components: { AudioWaveform },
  props: {
    // What is being recorded — read only, never stopped here
    stream: { type: MediaStream, required: true },
    // Freezes the track (no time passes on it) until set back
    paused: { type: Boolean, default: false }
  },
  data() {
    return {
      SLICES_PER_SECOND,
      // The recorded levels so far, as a view onto the history (a new view each frame redraws the waveform)
      view: null,
      windowStart: 0,
      width: 0,
      headX: 0
    }
  },
  computed: {
    // Seconds across the whole lane, at the fixed 30 px/s — so the newest bar lands right under the head
    windowSeconds() {
      return this.width / PIXELS_PER_SECOND
    },
    // Seconds that fit before the head's resting place
    visibleSeconds() {
      return Math.max(0, this.width - HEAD_MARGIN_PX) / PIXELS_PER_SECOND
    }
  },
  watch: {
    paused(isPaused) {
      if (isPaused) this.stop()
      else this.start()
    },
    stream() {
      this.connectSource()
    }
  },
  created() {
    // Audio graph + history — not reactive
    this.context = null
    this.analyser = null
    this.source = null
    this.samples = null
    this.rafId = null
    this.levels = new Float32Array(INITIAL_SLICES)
    this.count = 0
    this.slicePeak = 0
    this.activeSeconds = 0
    this.lastFrameAt = null
  },
  mounted() {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      this.context = new AudioContextClass()
      // A context made outside a user gesture can start suspended
      if (this.context.state === 'suspended' && this.context.resume) this.context.resume().catch(() => {})
      this.analyser = this.context.createAnalyser()
      this.analyser.fftSize = 2048
      this.samples = new Float32Array(this.analyser.fftSize)
      this.connectSource()
    } catch (err) {
      // No Web Audio here: the lane stays empty, the recording itself is unaffected
      console.error('LiveAudioTrack: could not listen to the stream', err.message)
      this.analyser = null
      return
    }
    if (!this.paused) this.start()
  },
  beforeUnmount() {
    this.stop()
    if (this.source) this.source.disconnect()
    if (this.analyser && this.analyser.disconnect) this.analyser.disconnect()
    if (this.context) {
      const closing = this.context.close()
      if (closing && closing.catch) closing.catch(() => {})
    }
  },
  methods: {
    // Listen to the current stream (a new one replaces the old source; the history carries on)
    connectSource() {
      if (!this.context || !this.analyser) return
      if (this.source) this.source.disconnect()
      this.source = this.context.createMediaStreamSource(this.stream)
      this.source.connect(this.analyser)
    },
    start() {
      if (this.rafId !== null || !this.analyser) return
      // Time spent paused never counts
      this.lastFrameAt = null
      const frame = (now) => {
        this.sample(now)
        this.rafId = requestAnimationFrame(frame)
      }
      this.rafId = requestAnimationFrame(frame)
    },
    stop() {
      if (this.rafId !== null) cancelAnimationFrame(this.rafId)
      this.rafId = null
      this.lastFrameAt = null
    },
    // One animation frame: advance the recording clock, close any finished slices, fold this frame's loudest
    // sample into the slice still filling, then place the window and the head
    sample(now) {
      if (!this.analyser) return
      if (this.lastFrameAt !== null) this.activeSeconds += Math.max(0, now - this.lastFrameAt) / 1000
      this.lastFrameAt = now

      // A hidden tab gets no frames; the slices it missed read as silence, so the track keeps the recording's length
      const finished = Math.floor(this.activeSeconds / SLICE_SECONDS)
      while (this.count < finished) {
        this.ensureCapacity(this.count + 1)
        this.levels[this.count] = this.slicePeak
        this.count++
        this.slicePeak = 0
      }

      this.analyser.getFloatTimeDomainData(this.samples)
      for (let i = 0; i < this.samples.length; i++) {
        const level = Math.abs(this.samples[i])
        if (level > this.slicePeak) this.slicePeak = level
      }
      this.ensureCapacity(this.count + 1)
      this.levels[this.count] = this.slicePeak
      this.view = this.levels.subarray(0, this.count + 1)

      this.windowStart = Math.max(0, this.activeSeconds - this.visibleSeconds)
      this.headX = (this.activeSeconds - this.windowStart) * PIXELS_PER_SECOND
    },
    // The whole history is kept (a 2-hour visit is ~290 KB); doubling keeps appends cheap
    ensureCapacity(size) {
      if (size <= this.levels.length) return
      const grown = new Float32Array(Math.max(size, this.levels.length * 2))
      grown.set(this.levels)
      this.levels = grown
    }
  }
}
</script>

<style scoped>
.live-audio-track {
  position: relative;
  height: 56px;
  margin-bottom: 1rem;
  border: 1px solid var(--color-border-med);
  border-radius: 6px;
  background: var(--color-fill-med);
  overflow: hidden;
}

/* The lane still to be recorded: a center line the waveform grows over */
.live-audio-track::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-top: 1px solid var(--color-border-med);
}

/* Two classes, so this outranks AudioWaveform's own full-size rule */
.live-audio-track .live-audio-track-wave {
  position: absolute;
  inset: 6px 0;
  width: auto;
  height: auto;
}

/* The record head: ScribeView's recording dot colors (red while recording, amber while paused) */
.live-audio-track-head {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 2px;
  margin-left: -1px;
  background: #ef4444;
  pointer-events: none;
}

.live-audio-track[data-paused="true"] .live-audio-track-head {
  background: #f59e0b;
}
</style>
